import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../../contexts/OrgContext';
import { useAuth } from '../../contexts/AuthContext';
import { getFlocs, updateFlocWithDescendants, toggleFlocHiddenWithDescendants } from '../../services/flocService';
import {
  FunctionalLocation,
  calculateRiskScore,
  getRiskLevel,
  getRiskColorClasses,
  getCriticalityValue,
  getConditionLabel,
  DEFAULT_RISK_MATRIX,
} from '@easydone/shared';
import { canBeChildOf } from '../../services/flocHierarchyRules';
import { ConfirmFlocMoveDialog } from '../../components/ConfirmFlocMoveDialog';
import {
  LocationFilterMap,
  filterItemsByLocation,
  formatDistance,
  LOCATION_RADIUS_OPTIONS,
  LocationData,
} from '../../components/LocationFilterMap';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  PlusIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  Bars3Icon,
  ChevronRightIcon,
  ChevronDownIcon,
  StarIcon,
  EyeIcon,
  EyeSlashIcon,
  MapIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

interface FlocWithLocation extends FunctionalLocation {
  distance?: number;
}

export function FlocsListPageEnhanced() {
  const { currentOrg } = useOrg();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [flocs, setFlocs] = useState<FunctionalLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterCriticality, setFilterCriticality] = useState<string>('all');
  const [filterCondition, setFilterCondition] = useState<string>('all');
  const [filterRisk, setFilterRisk] = useState<string>('all');
  const [showHidden, setShowHidden] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'criticality' | 'condition' | 'risk'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // View mode state
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');

  // Location state
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<LocationData | null>(null);
  const [locationRadius, setLocationRadius] = useState(LOCATION_RADIUS_OPTIONS[3].value); // Default 1km
  const [locationError, setLocationError] = useState('');
  const [locationWatchId, setLocationWatchId] = useState<number | null>(null);

  // Tree view state: tracks which FLOCs are expanded
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const lastClickedFlocRef = useRef<string | null>(null);

  // Favorites state: tracks favorite FLOCs per user
  const [favoriteFlocIds, setFavoriteFlocIds] = useState<Set<string>>(new Set());

  // Drag and drop state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [pendingMove, setPendingMove] = useState<{
    flocId: string;
    newParentId: string | null;
  } | null>(null);
  const [showMoveConfirm, setShowMoveConfirm] = useState(false);
  const [moving, setMoving] = useState(false);

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    })
  );

  useEffect(() => {
    loadFlocs();
  }, [currentOrg]);

  // Auto-enable location when switching to map view
  useEffect(() => {
    if (viewMode === 'map' && !locationEnabled && !currentLocation) {
      enableLocation();
    }
  }, [viewMode]);

  // Cleanup location watch on unmount
  useEffect(() => {
    return () => {
      if (locationWatchId !== null) {
        navigator.geolocation.clearWatch(locationWatchId);
      }
    };
  }, [locationWatchId]);

  // Scroll to show first child at top when expanding parent
  useEffect(() => {
    if (lastClickedFlocRef.current) {
      const clickedFlocId = lastClickedFlocRef.current;
      const isExpanded = expandedIds.has(clickedFlocId);

      // If expanding, find the first child and scroll it into view
      if (isExpanded) {
        // Small delay to ensure children are rendered in DOM
        setTimeout(() => {
          // Find the first child FLOC
          const firstChild = flocs.find(f => f.parentFlocId === clickedFlocId);

          if (firstChild) {
            const childElement = document.getElementById(`floc-row-${firstChild.id}`);
            if (childElement) {
              // Use 'center' to ensure it's visible, not hidden behind header
              childElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }, 50);
      }
      // If collapsing, keep current scroll position (do nothing)

      lastClickedFlocRef.current = null;
    }
  }, [expandedIds, flocs]);

  // Restore last viewed FLOC on page load
  useEffect(() => {
    if (flocs.length > 0 && currentUser) {
      const storageKey = `floc-last-viewed-${currentUser.uid}`;
      const lastViewedFlocId = localStorage.getItem(storageKey);

      if (lastViewedFlocId && flocs.some(f => f.id === lastViewedFlocId)) {
        // Expand path to the last viewed FLOC
        expandPathToFloc(lastViewedFlocId);

        // Scroll to it after a short delay to ensure DOM is ready
        setTimeout(() => {
          const element = document.getElementById(`floc-row-${lastViewedFlocId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
    }
  }, [flocs.length, currentUser]);

  // Load favorites from localStorage
  useEffect(() => {
    if (currentUser) {
      const storageKey = `floc-favorites-${currentUser.uid}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const favoriteIds = JSON.parse(stored) as string[];
          setFavoriteFlocIds(new Set(favoriteIds));
        } catch (err) {
          console.error('Failed to parse favorites:', err);
        }
      }
    }
  }, [currentUser]);

  const loadFlocs = async () => {
    if (!currentOrg) return;

    setLoading(true);
    setError('');

    try {
      const data = await getFlocs(currentOrg.id);
      setFlocs(data);
    } catch (err: any) {
      console.error('Failed to load FLOCs:', err);
      setError('Failed to load functional locations');
    } finally {
      setLoading(false);
    }
  };

  const enableLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationError('');
    // Use watchPosition for continuous location updates
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationEnabled(true);
        setLocationError('');
      },
      (error) => {
        setLocationEnabled(false);
        setCurrentLocation(null);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location unavailable');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out');
            break;
          default:
            setLocationError('Unknown location error');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
    setLocationWatchId(watchId);
  };

  const disableLocation = () => {
    if (locationWatchId !== null) {
      navigator.geolocation.clearWatch(locationWatchId);
      setLocationWatchId(null);
    }
    setLocationEnabled(false);
    setCurrentLocation(null);
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverId(event.over?.id as string | null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over || active.id === over.id) return;

    const draggedFlocId = active.id as string;
    const targetFlocId = over.id as string;

    // Find the dragged and target FLOCs
    const draggedFloc = flocs.find(f => f.id === draggedFlocId);
    const targetFloc = flocs.find(f => f.id === targetFlocId);

    if (!draggedFloc) return;

    // Set pending move and show confirmation
    setPendingMove({
      flocId: draggedFlocId,
      newParentId: targetFloc?.id || null
    });
    setShowMoveConfirm(true);
  };

  const handleCancelMove = () => {
    setShowMoveConfirm(false);
    setPendingMove(null);
  };

  const handleConfirmMove = async () => {
    if (!pendingMove || !currentOrg || !currentUser) return;

    setShowMoveConfirm(false);
    setMoving(true);

    try {
      const result = await updateFlocWithDescendants(
        currentOrg.id,
        pendingMove.flocId,
        pendingMove.newParentId,
        currentUser.uid
      );

      if (result.success) {
        alert(`Successfully moved FLOC and ${result.affectedCount - 1} descendant(s)`);
        await loadFlocs(); // Reload data
      } else {
        throw new Error(result.error || 'Move failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to move FLOC');
      alert('Failed to move FLOC: ' + (err.message || 'Unknown error'));
    } finally {
      setMoving(false);
      setPendingMove(null);
    }
  };

  // Validation helpers
  const isValidDropTarget = (draggedFloc: FunctionalLocation, targetFloc: FunctionalLocation | null): boolean => {
    // Check type hierarchy
    const typeCheck = canBeChildOf(draggedFloc.type, targetFloc?.type || null);
    if (!typeCheck.valid) return false;

    // Prevent dropping on self or descendants
    if (targetFloc && (
      targetFloc.id === draggedFloc.id ||
      targetFloc.path.includes(draggedFloc.id)
    )) {
      return false;
    }

    return true;
  };

  const getDescendantCount = (flocId: string): number => {
    return flocs.filter(f => f.path.includes(flocId)).length;
  };

  const getValidationError = (move: { flocId: string; newParentId: string | null } | null): string | undefined => {
    if (!move) return undefined;

    const draggedFloc = flocs.find(f => f.id === move.flocId);
    const targetFloc = move.newParentId ? flocs.find(f => f.id === move.newParentId) : null;

    if (!draggedFloc) return 'FLOC not found';

    const typeCheck = canBeChildOf(draggedFloc.type, targetFloc?.type || null);
    if (!typeCheck.valid) {
      return typeCheck.reason;
    }

    if (targetFloc && targetFloc.path.includes(draggedFloc.id)) {
      return 'Cannot move a FLOC into its own descendant';
    }

    return undefined;
  };

  // Toggle expand/collapse for a FLOC
  const toggleExpanded = (flocId: string, parentId: string | null) => {
    lastClickedFlocRef.current = flocId; // Remember which row was clicked
    setExpandedIds(prev => {
      const newExpanded = new Set(prev);

      if (newExpanded.has(flocId)) {
        // Collapse: remove this FLOC from expanded set
        newExpanded.delete(flocId);
      } else {
        // Expand: add this FLOC to expanded set
        newExpanded.add(flocId);

        // Auto-collapse siblings (accordion behavior)
        if (parentId) {
          // Find all siblings and collapse them
          const siblings = flocs.filter(f => f.parentFlocId === parentId && f.id !== flocId);
          siblings.forEach(sibling => {
            newExpanded.delete(sibling.id);
            // Also collapse all descendants of siblings
            collapseDescendants(sibling.id, newExpanded);
          });
        } else {
          // Root level: collapse all other root-level FLOCs
          const rootSiblings = flocs.filter(f => !f.parentFlocId && f.id !== flocId);
          rootSiblings.forEach(sibling => {
            newExpanded.delete(sibling.id);
            collapseDescendants(sibling.id, newExpanded);
          });
        }
      }

      return newExpanded;
    });
  };

  // Helper to collapse all descendants of a FLOC
  const collapseDescendants = (flocId: string, expandedSet: Set<string>) => {
    const children = flocs.filter(f => f.parentFlocId === flocId);
    children.forEach(child => {
      expandedSet.delete(child.id);
      collapseDescendants(child.id, expandedSet);
    });
  };

  // Expand all FLOCs with children
  const expandAll = () => {
    const allWithChildren = flocs.filter(f => {
      return flocs.some(child => child.parentFlocId === f.id);
    });
    setExpandedIds(new Set(allWithChildren.map(f => f.id)));
  };

  // Collapse all FLOCs
  const collapseAll = () => {
    setExpandedIds(new Set());
  };

  // Expand path to a specific FLOC (all its ancestors)
  const expandPathToFloc = (flocId: string) => {
    const floc = flocs.find(f => f.id === flocId);
    if (!floc) return;

    const ancestorIds = new Set<string>();

    // Traverse up the tree to find all ancestors
    let currentFloc: FunctionalLocation | undefined = floc;
    while (currentFloc && currentFloc.parentFlocId) {
      ancestorIds.add(currentFloc.parentFlocId);
      currentFloc = flocs.find(f => f.id === currentFloc!.parentFlocId);
    }

    setExpandedIds(ancestorIds);
  };

  // Toggle favorite status for a FLOC
  const toggleFavorite = (flocId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click

    if (!currentUser) return;

    setFavoriteFlocIds(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(flocId)) {
        newFavorites.delete(flocId);
      } else {
        newFavorites.add(flocId);
      }

      // Save to localStorage
      const storageKey = `floc-favorites-${currentUser.uid}`;
      localStorage.setItem(storageKey, JSON.stringify(Array.from(newFavorites)));

      return newFavorites;
    });
  };

  // Toggle hidden status for a FLOC
  const toggleHidden = async (floc: FunctionalLocation, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click

    if (!currentOrg || !currentUser) return;

    const newHiddenState = !floc.hidden;

    // Show confirmation if hiding and has children
    if (newHiddenState && (floc.childCount || 0) > 0) {
      const confirmed = confirm(
        `This will hide ${floc.name} and all ${floc.childCount} child location${floc.childCount !== 1 ? 's' : ''}. Continue?`
      );
      if (!confirmed) return;
    }

    try {
      const result = await toggleFlocHiddenWithDescendants(
        currentOrg.id,
        floc.id,
        newHiddenState,
        currentUser.uid
      );

      if (result.success) {
        // Reload FLOCs to reflect changes
        await loadFlocs();

        const message = newHiddenState
          ? `Hidden ${result.affectedCount} location${result.affectedCount !== 1 ? 's' : ''}`
          : `Unhidden ${result.affectedCount} location${result.affectedCount !== 1 ? 's' : ''}`;

        alert(message);
      } else {
        throw new Error(result.error || 'Failed to update hidden status');
      }
    } catch (error: any) {
      console.error('Failed to toggle hidden status:', error);
      alert('Failed to update hidden status: ' + (error.message || 'Unknown error'));
    }
  };

  // Build hierarchical tree structure
  const buildFlocTree = (flocs: FunctionalLocation[]): FunctionalLocation[] => {
    const flocMap = new Map<string, FunctionalLocation & { children?: FunctionalLocation[] }>();
    const roots: (FunctionalLocation & { children?: FunctionalLocation[] })[] = [];

    // Create map of all FLOCs
    flocs.forEach(floc => {
      flocMap.set(floc.id, { ...floc, children: [] });
    });

    // Build tree by connecting parents and children
    flocs.forEach(floc => {
      const node = flocMap.get(floc.id)!;
      if (floc.parentFlocId && flocMap.has(floc.parentFlocId)) {
        const parent = flocMap.get(floc.parentFlocId)!;
        if (!parent.children) parent.children = [];
        parent.children.push(node);
      } else {
        // Root level FLOC (no parent or parent not found)
        roots.push(node);
      }
    });

    // Sort roots and recursively sort children
    const sortFlocs = (flocList: (FunctionalLocation & { children?: FunctionalLocation[] })[]) => {
      flocList.sort((a, b) => {
        // Favorites come first
        const aIsFavorite = favoriteFlocIds.has(a.id);
        const bIsFavorite = favoriteFlocIds.has(b.id);
        if (aIsFavorite && !bIsFavorite) return -1;
        if (!aIsFavorite && bIsFavorite) return 1;

        // Then sort by selected field
        let comparison = 0;
        switch (sortBy) {
          case 'criticality':
            comparison = getCriticalityValue(a.criticality) - getCriticalityValue(b.criticality);
            break;
          case 'condition':
            comparison = (a.conditionScore || 0) - (b.conditionScore || 0);
            break;
          case 'risk':
            const riskA = calculateRiskScore(a.criticality, a.conditionScore);
            const riskB = calculateRiskScore(b.criticality, b.conditionScore);
            comparison = riskA - riskB;
            break;
          default: // name
            comparison = a.code.localeCompare(b.code);
        }

        // Apply sort direction
        return sortDirection === 'asc' ? comparison : -comparison;
      });
      flocList.forEach(floc => {
        if (floc.children && floc.children.length > 0) {
          sortFlocs(floc.children);
        }
      });
    };

    sortFlocs(roots);

    // Flatten tree to array, but only include visible FLOCs (expanded parents)
    const flattenTree = (
      nodes: (FunctionalLocation & { children?: FunctionalLocation[] })[],
      depth: number = 0
    ): Array<FunctionalLocation & { depth: number; hasChildren: boolean; isExpanded: boolean }> => {
      const result: Array<FunctionalLocation & { depth: number; hasChildren: boolean; isExpanded: boolean }> = [];
      nodes.forEach(node => {
        const { children, ...floc } = node;
        const hasChildren = children && children.length > 0;
        const isExpanded = expandedIds.has(floc.id);

        result.push({
          ...(floc as FunctionalLocation),
          depth,
          hasChildren: hasChildren || false,
          isExpanded,
        });

        // Only include children if this node is expanded
        if (hasChildren && isExpanded) {
          result.push(...flattenTree(children!, depth + 1));
        }
      });
      return result;
    };

    return flattenTree(roots);
  };

  const getFilteredFlocs = (): FlocWithLocation[] => {
    let filtered = flocs.filter(floc => {
      const matchesSearch =
        floc.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        floc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        floc.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = filterType === 'all' || floc.type === filterType;
      const matchesCriticality = filterCriticality === 'all' || floc.criticality === filterCriticality;

      // Condition filter
      const matchesCondition = filterCondition === 'all' ||
        (floc.conditionScore && floc.conditionScore.toString() === filterCondition);

      // Risk filter
      let matchesRisk = true;
      if (filterRisk !== 'all') {
        const riskScore = calculateRiskScore(floc.criticality, floc.conditionScore);
        const riskLevel = getRiskLevel(riskScore, currentOrg?.settings?.riskMatrix);
        matchesRisk = riskLevel === filterRisk;
      }

      const matchesHidden = showHidden || !floc.hidden; // Hide hidden FLOCs by default

      return matchesSearch && matchesType && matchesCriticality && matchesCondition && matchesRisk && matchesHidden;
    });

    // Apply location filter if enabled (for map view)
    if (viewMode === 'map' && locationEnabled && currentLocation) {
      filtered = filterItemsByLocation(filtered, currentLocation, locationRadius);
    }

    return filtered;
  };

  const filteredFlocs = viewMode === 'list'
    ? buildFlocTree(getFilteredFlocs())
    : getFilteredFlocs();

  const getCriticalityColor = (criticality: string) => {
    switch (criticality) {
      case 'very_high':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-blue-100 text-blue-800';
      case 'very_low':
        return 'bg-green-100 text-green-800';
      case 'not_in_use':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'non_operational':
        return 'bg-red-100 text-red-800';
      case 'vacant':
        return 'bg-gray-100 text-gray-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'site':
      case 'building':
        return <BuildingOfficeIcon className="w-5 h-5" />;
      case 'area':
      case 'zone':
      case 'equipment_position':
        return <MapPinIcon className="w-5 h-5" />;
      default:
        return <MapPinIcon className="w-5 h-5" />;
    }
  };

  // Render marker popup for map view
  const renderMarkerPopup = (floc: FlocWithLocation) => (
    <div className="text-sm">
      <div className="font-semibold mb-1">{floc.name}</div>
      <div className="text-xs text-gray-600 mb-1">
        Code: {floc.code}
      </div>
      <div className="text-xs text-gray-600 mb-1">
        Type: {floc.type.replace('_', ' ')}
      </div>
      <div className="text-xs mb-1">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getCriticalityColor(floc.criticality)}`}>
          {floc.criticality.toUpperCase()}
        </span>
      </div>
      {floc.conditionScore && (
        <div className="text-xs text-gray-600 mb-1">
          Condition: {floc.conditionScore} - {getConditionLabel(floc.conditionScore)}
        </div>
      )}
      {floc.conditionScore && (
        <>
          {(() => {
            const riskScore = calculateRiskScore(floc.criticality, floc.conditionScore);
            const riskLevel = getRiskLevel(riskScore, currentOrg?.settings?.riskMatrix);
            const colorClasses = getRiskColorClasses(riskLevel);
            return (
              <div className="text-xs mb-2">
                Risk: <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses.bg} ${colorClasses.text} ${colorClasses.border} border`}>
                  {riskLevel.toUpperCase()}
                </span>
              </div>
            );
          })()}
        </>
      )}
      {floc.distance && (
        <div className="text-xs text-blue-600 mb-2">
          Distance: {formatDistance(floc.distance)}
        </div>
      )}
      <button
        onClick={() => navigate(`/flocs/${floc.id}`)}
        className="text-xs text-blue-600 hover:underline"
      >
        View Details →
      </button>
    </div>
  );

  // Draggable row component
  const DraggableFlocRow = ({ floc }: { floc: FunctionalLocation }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: floc.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const draggedFloc = activeId ? flocs.find(f => f.id === activeId) : null;
    const isValidTarget = draggedFloc ? isValidDropTarget(draggedFloc, floc) : true;
    const isOver = overId === floc.id;
    const isExpanded = (floc as any).isExpanded;
    const hasChildren = (floc as any).hasChildren;

    return (
      <tr
        id={`floc-row-${floc.id}`}
        ref={setNodeRef}
        style={style}
        className={`
          hover:bg-gray-50 cursor-pointer transition-colors
          ${isDragging ? 'opacity-50' : ''}
          ${isExpanded && hasChildren ? 'bg-blue-50 border-l-4 border-blue-400' : ''}
          ${isOver && isValidTarget ? 'bg-green-50 border-l-4 border-green-500' : ''}
          ${isOver && !isValidTarget ? 'bg-red-50 border-l-4 border-red-500' : ''}
        `}
        onClick={() => {
          // Store last viewed FLOC
          if (currentUser) {
            localStorage.setItem(`floc-last-viewed-${currentUser.uid}`, floc.id);
          }
          navigate(`/flocs/${floc.id}`);
        }}
      >
        {/* Drag Handle Column */}
        <td className="px-2 py-4">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab hover:text-primary-600 active:cursor-grabbing"
            onClick={(e) => e.stopPropagation()}
          >
            <Bars3Icon className="w-5 h-5 text-gray-400" />
          </div>
        </td>

        {/* FLOC Name Column */}
        <td className="px-6 py-4">
          <div className="flex items-center">
            {/* Hierarchy indentation */}
            <div style={{ width: `${(floc as any).depth * 24}px` }} className="flex-shrink-0" />

            {/* Expand/Collapse Button */}
            {(floc as any).hasChildren ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(floc.id, floc.parentFlocId || null);
                }}
                className="mr-2 p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
              >
                {(floc as any).isExpanded ? (
                  <ChevronDownIcon className="w-4 h-4 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4 text-gray-600" />
                )}
              </button>
            ) : (
              <div className="w-6 mr-2 flex-shrink-0" />
            )}

            <div className="text-gray-400 mr-3 flex-shrink-0">
              {getTypeIcon(floc.type)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {floc.name}
                </div>
                {floc.level > 0 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 flex-shrink-0">
                    L{floc.level}
                  </span>
                )}
                {floc.childCount !== undefined && floc.childCount > 0 && (
                  <span className="text-xs text-gray-500 flex-shrink-0">
                    ({floc.childCount} child{floc.childCount !== 1 ? 'ren' : ''})
                  </span>
                )}
                {/* Favorite Star */}
                <button
                  onClick={(e) => toggleFavorite(floc.id, e)}
                  className="ml-2 p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                  title={favoriteFlocIds.has(floc.id) ? 'Remove from favorites' : 'Add to favorites'}
                >
                  {favoriteFlocIds.has(floc.id) ? (
                    <StarIconSolid className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <StarIcon className="w-4 h-4 text-gray-400 hover:text-yellow-500" />
                  )}
                </button>
                {/* Hide/Unhide */}
                <button
                  onClick={(e) => toggleHidden(floc, e)}
                  className="p-1 hover:bg-gray-200 rounded transition-colors flex-shrink-0"
                  title={floc.hidden ? 'Unhide this FLOC' : 'Hide this FLOC'}
                >
                  {floc.hidden ? (
                    <EyeSlashIcon className="w-4 h-4 text-gray-500" />
                  ) : (
                    <EyeIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
              <div className="text-sm text-gray-500 truncate">{floc.code}</div>
            </div>
          </div>
        </td>

        {/* Type Column */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span className="text-sm text-gray-900 capitalize">
            {floc.type.replace('_', ' ')}
          </span>
        </td>

        {/* Criticality Column */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCriticalityColor(
              floc.criticality
            )}`}
          >
            {floc.criticality.toUpperCase()}
          </span>
        </td>

        {/* Condition Column */}
        <td className="px-6 py-4 whitespace-nowrap">
          {floc.conditionScore ? (
            <span className="text-sm text-gray-900">
              {floc.conditionScore} - {getConditionLabel(floc.conditionScore)}
            </span>
          ) : (
            <span className="text-sm text-gray-400">Not assessed</span>
          )}
        </td>

        {/* Risk Score Column */}
        <td className="px-6 py-4 whitespace-nowrap">
          {floc.conditionScore ? (
            <>
              {(() => {
                const riskScore = calculateRiskScore(floc.criticality, floc.conditionScore);
                const riskLevel = getRiskLevel(riskScore, currentOrg?.settings?.riskMatrix);
                const colorClasses = getRiskColorClasses(riskLevel);
                const threshold = currentOrg?.settings?.riskMatrix?.thresholds[riskLevel] ||
                  DEFAULT_RISK_MATRIX.thresholds[riskLevel];

                return (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">
                      {riskScore}
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClasses.bg} ${colorClasses.text} ${colorClasses.border} border`}
                      title={`${threshold.label} - ${threshold.action}`}
                    >
                      {riskLevel.toUpperCase()}
                    </span>
                  </div>
                );
              })()}
            </>
          ) : (
            <span className="text-sm text-gray-400">-</span>
          )}
        </td>

        {/* Status Column */}
        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
              floc.operationalStatus
            )}`}
          >
            {floc.operationalStatus.replace('_', ' ')}
          </span>
        </td>

        {/* Installed Asset Column */}
        <td className="px-6 py-4 text-sm text-gray-900">
          {floc.installedAssetId ? (
            <span className="text-blue-600">Asset Installed</span>
          ) : (
            <span className="text-gray-400">Vacant</span>
          )}
        </td>

        {/* Actions Column */}
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/flocs/${floc.id}`);
            }}
            className="text-primary-600 hover:text-primary-900"
          >
            View
          </button>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading functional locations...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Functional Locations</h1>
            <p className="text-gray-600 mt-1">
              Manage your facility hierarchy and equipment positions
            </p>
          </div>
          <div className="flex gap-2">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <ListBulletIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'map'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MapIcon className="w-5 h-5" />
              </button>
            </div>

            <button
              onClick={() => navigate('/flocs/new')}
              className="btn btn-primary flex items-center"
            >
              <PlusIcon className="w-5 h-5 mr-2" />
              New FLOC
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by code or name..."
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="input w-full"
            >
              <option value="all">All Types</option>
              <option value="site">Site</option>
              <option value="building">Building</option>
              <option value="floor">Floor</option>
              <option value="area">Area</option>
              <option value="zone">Zone</option>
              <option value="system">System</option>
              <option value="equipment_position">Equipment Position</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Criticality
            </label>
            <select
              value={filterCriticality}
              onChange={(e) => setFilterCriticality(e.target.value)}
              className="input w-full"
            >
              <option value="all">All Criticality</option>
              <option value="very_high">Very High</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="very_low">Very Low</option>
              <option value="not_in_use">Not in Use</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Condition
            </label>
            <select
              value={filterCondition}
              onChange={(e) => setFilterCondition(e.target.value)}
              className="input w-full"
            >
              <option value="all">All Conditions</option>
              <option value="1">Very good (1)</option>
              <option value="2">Good (2)</option>
              <option value="3">Moderate (3)</option>
              <option value="4">Poor (4)</option>
              <option value="5">Very poor (5)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Risk Level
            </label>
            <select
              value={filterRisk}
              onChange={(e) => setFilterRisk(e.target.value)}
              className="input w-full"
            >
              <option value="all">All Risk Levels</option>
              <option value="low">Low (Green)</option>
              <option value="medium">Medium (Yellow)</option>
              <option value="high">High (Orange)</option>
              <option value="critical">Critical (Red)</option>
            </select>
          </div>
        </div>

        {/* Sort Controls */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'criticality' | 'condition' | 'risk')}
                className="input w-full"
              >
                <option value="name">Name (Code)</option>
                <option value="criticality">Criticality</option>
                <option value="condition">Condition Score</option>
                <option value="risk">Risk Score</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sort Direction
              </label>
              <select
                value={sortDirection}
                onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
                className="input w-full"
              >
                <option value="asc">Ascending (Low to High)</option>
                <option value="desc">Descending (High to Low)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Show Hidden FLOCs Toggle */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showHidden}
              onChange={(e) => setShowHidden(e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">
              Show hidden FLOCs
            </span>
            <span className="ml-2 text-xs text-gray-500">
              (Hidden FLOCs are excluded from standard views)
            </span>
          </label>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <LocationFilterMap
          items={filteredFlocs as FlocWithLocation[]}
          locationEnabled={locationEnabled}
          currentLocation={currentLocation}
          locationRadius={locationRadius}
          locationError={locationError}
          onEnableLocation={enableLocation}
          onDisableLocation={disableLocation}
          onRadiusChange={setLocationRadius}
          renderMarkerPopup={renderMarkerPopup}
          onItemClick={(floc) => navigate(`/flocs/${floc.id}`)}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Tree View Controls */}
          {filteredFlocs.length > 0 && (
            <div className="mb-4 flex items-center gap-2">
              <button
                onClick={expandAll}
                className="btn btn-secondary text-sm flex items-center"
              >
                <ChevronDownIcon className="w-4 h-4 mr-1" />
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="btn btn-secondary text-sm flex items-center"
              >
                <ChevronRightIcon className="w-4 h-4 mr-1" />
                Collapse All
              </button>
            </div>
          )}

          {/* FLOCs List */}
          {filteredFlocs.length === 0 ? (
            <div className="card text-center py-12">
              <BuildingOfficeIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No functional locations found
              </h3>
              <p className="text-gray-600 mb-6">
                {searchTerm || filterType !== 'all' || filterCriticality !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by creating your first functional location'}
              </p>
              {!searchTerm && filterType === 'all' && filterCriticality === 'all' && (
                <button
                  onClick={() => navigate('/flocs/new')}
                  className="btn btn-primary inline-flex items-center"
                >
                  <PlusIcon className="w-5 h-5 mr-2" />
                  Create Functional Location
                </button>
              )}
            </div>
          ) : (
            <div className="card p-0">
              <div className="overflow-x-auto">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={filteredFlocs.map(f => f.id)} strategy={verticalListSortingStrategy}>
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {/* Drag handle column */}
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            FLOC
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Criticality
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Condition
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Risk Score
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Installed Asset
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredFlocs.map((floc) => (
                          <DraggableFlocRow key={floc.id} floc={floc} />
                        ))}
                      </tbody>
                    </table>
                  </SortableContext>
                  <DragOverlay>
                    {activeId && (
                      <div className="bg-white shadow-lg rounded-lg p-4 border-2 border-primary-500 opacity-90">
                        <div className="font-medium text-gray-900">
                          {flocs.find(f => f.id === activeId)?.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {flocs.find(f => f.id === activeId)?.code}
                        </div>
                      </div>
                    )}
                  </DragOverlay>
                </DndContext>
              </div>
            </div>
          )}
        </>
      )}

      {/* Confirmation Dialog */}
      {showMoveConfirm && pendingMove && (
        <ConfirmFlocMoveDialog
          isOpen={showMoveConfirm}
          onClose={handleCancelMove}
          onConfirm={handleConfirmMove}
          movingFloc={flocs.find(f => f.id === pendingMove.flocId)!}
          targetParent={pendingMove.newParentId ? flocs.find(f => f.id === pendingMove.newParentId) || null : null}
          descendantCount={getDescendantCount(pendingMove.flocId)}
          isValidMove={!getValidationError(pendingMove)}
          validationError={getValidationError(pendingMove)}
        />
      )}

      {/* Loading overlay during move */}
      {moving && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="text-lg font-medium text-gray-900 mb-2">Moving FLOC...</div>
            <div className="text-sm text-gray-600">Please wait while we update the hierarchy.</div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="text-sm text-gray-600">Total FLOCs</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{flocs.length}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600">With Assets</div>
          <div className="text-2xl font-bold text-blue-600 mt-1">
            {flocs.filter((f) => f.installedAssetId).length}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600">Vacant</div>
          <div className="text-2xl font-bold text-gray-400 mt-1">
            {flocs.filter((f) => !f.installedAssetId).length}
          </div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-600">Very High</div>
          <div className="text-2xl font-bold text-red-600 mt-1">
            {flocs.filter((f) => f.criticality === 'very_high').length}
          </div>
        </div>
      </div>
    </div>
  );
}
